import { create } from 'zustand';
import type {
  ActivationSubStepId,
  OpenDrawer,
  ActivationTransition,
} from '@config/spaceActivation';
import { createGrantFlow } from '@lib/overlay/grantFlow';

/** The celebration drawers — the only kinds that can be queued as pending. */
type PendingCelebration = Extract<OpenDrawer, { kind: 'stepCelebration' | 'activationComplete' }>;

/** A queued celebration plus the time it was earned, for the freshness check. */
type QueuedCelebration = PendingCelebration & { queuedAt: number };

/** The two instructional-drawer kinds a sub-step opens directly (never queued). */
type DrawerRequest = Extract<OpenDrawer, { kind: 'subStep' | 'video' }>;

/**
 * A celebration is shown only if the owner reaches the activation tab within this window
 * of earning it; staler entries are discarded on consume. Bounds the "earned off-tab then
 * fires detached on a much later visit" case without dropping the happy path (complete →
 * returnTo activation tab → consume within seconds).
 */
const CELEBRATION_FRESHNESS_MS = 5 * 60 * 1000;

/**
 * The action-flow token is honored only if the action site is reached within this window of
 * arming it. The activation entry (CommunityDetail) arms the token then opens a choice drawer
 * or navigates; if the owner abandons before reaching the action site (e.g. dismisses the
 * create-event choice drawer), the token would otherwise linger and mis-attribute a much later
 * manual action in the same space to the activation flow. 5 min is generous enough that a real
 * arm → (Luma import / build event) → reach-site path never expires mid-flow.
 */
const ACTION_FLOW_FRESHNESS_MS = 5 * 60 * 1000;

/**
 * The instructional sub-step/video drawers' turn on the overlay mutex (overlay-system-design
 * §6.3, §6.6, §6.8 — activation joins). Kept separate from the celebration id below so a step
 * or activation-complete celebration earned while a drawer is open QUEUES behind it instead of
 * overwriting `open` — the unconditional write §6.6 flagged.
 */
const ACTIVATION_DRAWER_GRANT_ID = 'activation:drawer';
/** The step/activation-complete celebrations' turn on the overlay mutex. */
export const ACTIVATION_CELEBRATION_GRANT_ID = 'activation:celebration';

/** Maps a queued celebration to the `open` shape GlobalActivationDrawers renders. */
const celebrationToOpen = (c: QueuedCelebration): OpenDrawer =>
  c.kind === 'activationComplete'
    ? { kind: 'activationComplete', spaceId: c.spaceId }
    : { kind: 'stepCelebration', id: c.id, spaceId: c.spaceId };

/**
 * Whether `a` should be shown over `b`: activation-complete beats a step celebration;
 * otherwise the later `queuedAt` wins (ties favor `a`, using `>=`).
 */
const outranks = (a: QueuedCelebration, b: QueuedCelebration): boolean => {
  if (a.kind === 'activationComplete' && b.kind !== 'activationComplete') return true;
  if (b.kind === 'activationComplete' && a.kind !== 'activationComplete') return false;
  return a.queuedAt >= b.queuedAt;
};

/**
 * Global overlay state for the Space Activation feature. Holds the single
 * activation drawer (sub-step, step celebration, or activation-complete) that
 * is currently open, so any surface can open one without prop-drilling.
 *
 * `advanceToNext` is intentionally NOT modelled here — chaining one drawer
 * into the next is owned by the UI session that drives the checklist.
 */
interface ActivationStore {
  open: OpenDrawer | null;
  /**
   * Step/activation celebrations EARNED but not yet shown — a bounded freshness queue.
   *
   * The completing mutation can fire on a side-trip surface (the invite page, an
   * instructional drawer) — painting the celebration straight into `open` would drop it
   * over the wrong screen (e.g. on top of the invite share sheet). So we queue it here;
   * the activation tab consumes on mount via `consumePendingCelebration`, guaranteeing the
   * "you did it" moment lands on the activation timeline, not on whatever surface fired
   * completion. Each entry is timestamped: a celebration the owner never returned for is
   * discarded once stale (CELEBRATION_FRESHNESS_MS) instead of firing detached on a much
   * later visit, and a second milestone earned before the first is consumed is kept, not
   * silently overwritten.
   */
  pendingCelebrations: QueuedCelebration[];
  /**
   * One-time milestone keys (`${spaceId}:substep|step|activation:<id>`) whose effects —
   * analytics + celebration enqueue — have already fired this session. A counter sub-step
   * finished by two near-simultaneous POSTs (the announce design+text double-tap) resolves
   * BOTH mutations' onSuccess with a server response that has crossed the threshold, so
   * without this guard each milestone double-emits into the activation funnel (K-factor).
   * `claimMilestone` makes each milestone's emission exactly-once.
   */
  claimedMilestones: Set<string>;
  /**
   * The activation action-completion expected from the next action-site visit
   * (e.g. seed-posts → Create Question). Set ONLY by the admin-gated activation
   * entry, so an action site can confirm "this post is part of the owner's
   * activation flow" WITHOUT trusting a spoofable URL param. Consumed once on use.
   * `armedAt` (a Date.now() stamp) bounds how long it stays honorable: a token armed
   * then abandoned before reaching the action site goes stale (ACTION_FLOW_FRESHNESS_MS)
   * via `matchesActionFlow`, so it can't mis-attribute a much later manual action.
   */
  actionFlow: { subStepId: ActivationSubStepId; spaceId: string; armedAt: number } | null;
  /**
   * The instructional drawer requested or on screen. Written only by `requestDrawer`,
   * never read by anything outside this store. Not cleared when the flow ends -- it keeps
   * the last payload indefinitely, so any read of this field must gate on `drawerFlow`
   * being non-idle (mirrors `_requestedCelebration` below).
   */
  _requestedDrawer: DrawerRequest | null;
  /**
   * The celebration requested or on screen. Written only by `consumePendingCelebration`.
   * Not cleared when the flow ends -- it keeps the last payload indefinitely, so any read
   * of this field must gate on `celebrationFlow` being non-idle (see `stillNeeded` in
   * `consumePendingCelebration` for the reader that enforces it).
   */
  _requestedCelebration: QueuedCelebration | null;
  openSubStepDrawer: (
    id: ActivationSubStepId,
    spaceId: string,
    read?: { identifier: string; inviteToken?: string | null },
    onPrimaryAction?: () => void,
    onSecondaryAction?: () => void,
  ) => void;
  /** Open the fullscreen video player for a video sub-step (e.g. watch_video). */
  openVideo: (id: ActivationSubStepId, spaceId: string) => void;
  /** Queue the celebration earned by `t` (does NOT show it — see `pendingCelebrations`). */
  openCelebration: (t: ActivationTransition, spaceId: string) => void;
  /**
   * Atomically claim a one-time milestone `key`. Returns true only the FIRST time a key is
   * seen this session; a racing duplicate completion gets false so the caller skips that
   * milestone's analytics + celebration. See `claimedMilestones`.
   */
  claimMilestone: (key: string) => boolean;
  /** Promote a queued celebration into `open`, but only one earned for THIS space. */
  consumePendingCelebration: (spaceId: string) => void;
  /**
   * Closes what is on screen. If a flow is HELD, only that flow is ended -- a sibling
   * merely PENDING behind it survives, so the held flow's own dismiss grants it next
   * (symmetric: a queued drawer behind a held celebration survives exactly like a queued
   * celebration behind a held drawer). If neither flow is held, falls through to
   * `closeAll`, which drops anything merely queued on both. A CTA that navigates away or
   * opens another local surface instead of just dismissing should use
   * `closeDeferringCelebration`, so a celebration merely pending doesn't land on whatever
   * the CTA opens.
   */
  close: (reason?: 'user' | 'programmatic') => void;
  /**
   * Like `close()`, but a celebration merely PENDING (not held) is hidden and handed back
   * to `pendingCelebrations` instead of being let through by the drain -- for a CTA that
   * navigates away or opens another local surface, where a granted celebration would show
   * over the wrong screen. The activation tab's own consume effect re-offers it if the
   * owner returns within the freshness window.
   */
  closeDeferringCelebration: () => void;
  /**
   * Closes what is on screen and ends both flows regardless of held/pending, for the
   * auth-route reset (`transientOverlays`): nothing the previous screen asked for may land
   * on the door. `pendingCelebrations` survives by design -- it's a queue of earned-but-
   * unshown celebrations, not overlay state, and the tab that owns one re-offers it later.
   */
  closeAll: () => void;
  /** The video/sub-step drawer's Android back path for the held grant; null clears it. */
  setBackHandler: (fn: (() => void) | null) => void;
  setActionFlow: (subStepId: ActivationSubStepId, spaceId: string) => void;
  clearActionFlow: () => void;
}

/**
 * The video/sub-step drawer's Android back path for the held grant; set by whichever
 * drawer is currently mounted (mirrors shareSheetStore's module-level `backHandler`).
 */
let drawerBackHandler: (() => void) | null = null;

export const useActivationStore = create<ActivationStore>((set, get) => {
  const drawerFlow = createGrantFlow(ACTIVATION_DRAWER_GRANT_ID, {
    onGranted: () => {
      set(s => ({ open: s._requestedDrawer }));
    },
    onClosed: () => {
      // Only a drawer-kind `open` is cleared here; `_requestedDrawer` is left as-is.
      set(s => ({
        open: s.open && (s.open.kind === 'subStep' || s.open.kind === 'video') ? null : s.open,
      }));
    },
  });

  const celebrationFlow = createGrantFlow(ACTIVATION_CELEBRATION_GRANT_ID, {
    onGranted: () => {
      set(s => ({
        open: s._requestedCelebration ? celebrationToOpen(s._requestedCelebration) : null,
      }));
    },
    onClosed: () => {
      set(s => ({
        open:
          s.open && (s.open.kind === 'stepCelebration' || s.open.kind === 'activationComplete')
            ? null
            : s.open,
      }));
    },
  });

  /** held -> swap `open` under the live grant; idle -> take a place in line; pending -> the
   * payload write above already covers a re-entry while queued. */
  const requestDrawer = (drawer: DrawerRequest) => {
    set({ _requestedDrawer: drawer });
    const state = drawerFlow.getState();
    if (state === 'held') {
      set({ open: drawer });
    } else if (state === 'idle') {
      drawerFlow.request({
        onBackButton: () => {
          if (drawerBackHandler) drawerBackHandler();
          else drawerFlow.end('user');
        },
      });
    }
  };

  return {
    open: null,
    pendingCelebrations: [],
    claimedMilestones: new Set(),
    actionFlow: null,
    _requestedDrawer: null,
    _requestedCelebration: null,

    openSubStepDrawer: (id, spaceId, read, onPrimaryAction, onSecondaryAction) => {
      requestDrawer({
        kind: 'subStep',
        id,
        spaceId,
        readIdentifier: read?.identifier,
        readInviteToken: read?.inviteToken,
        onPrimaryAction,
        onSecondaryAction,
      });
    },
    openVideo: (id, spaceId) => {
      requestDrawer({ kind: 'video', id, spaceId });
    },
    openCelebration: (t, spaceId) => {
      // Queue the celebration rather than showing it — the activation tab promotes it once
      // mounted (see `pendingCelebrations`). Activation-complete outranks a per-step
      // celebration when both cross in one transition. Prune stale entries on the way in so
      // the queue can't grow unbounded across a long session.
      let earned: PendingCelebration | null = null;
      if (t.activationJustCompleted) {
        earned = { kind: 'activationComplete', spaceId };
      } else if (t.stepJustCompleted) {
        earned = { kind: 'stepCelebration', id: t.stepJustCompleted, spaceId };
      }
      if (!earned) return;
      const queued = earned;
      const now = Date.now();
      set(state => ({
        pendingCelebrations: [
          ...state.pendingCelebrations.filter(c => now - c.queuedAt < CELEBRATION_FRESHNESS_MS),
          { ...queued, queuedAt: now },
        ],
      }));
    },
    claimMilestone: key => {
      // Single-threaded JS: the two racing onSuccess callbacks run to completion one after
      // the other, so this read-then-write is atomic between them — the first claims, the
      // second sees the key and returns false.
      if (get().claimedMilestones.has(key)) return false;
      set(state => ({ claimedMilestones: new Set(state.claimedMilestones).add(key) }));
      return true;
    },
    consumePendingCelebration: spaceId => {
      // Promote a queued celebration for THIS space into the celebration flow's request —
      // ONLY for this space (the queue is global) and ONLY while still fresh. Prunes ALL of
      // this space's entries (fresh or stale) so nothing re-pops later. Goes through the
      // grant flow rather than writing `open` unconditionally, so it queues behind a held
      // drawer rather than clobbering it.
      const state = get();
      const mine = state.pendingCelebrations.filter(c => c.spaceId === spaceId);
      const remaining = state.pendingCelebrations.filter(c => c.spaceId !== spaceId);
      const now = Date.now();
      const fresh = mine.filter(c => now - c.queuedAt < CELEBRATION_FRESHNESS_MS);

      // The in-flight request for this space also counts as a candidate, so a fresher/
      // higher-rank milestone earned while the first is still queued or on screen can
      // outrank and replace it. A request belonging to ANOTHER space does NOT count here --
      // handled separately below, so this space's winner never clobbers it.
      const requested = state._requestedCelebration;
      const flowState = celebrationFlow.getState();
      const requestedCounts =
        requested !== null && requested.spaceId === spaceId && flowState !== 'idle';
      const candidates = requestedCounts ? [...fresh, requested] : fresh;

      if (candidates.length === 0) {
        if (mine.length > 0) set({ pendingCelebrations: remaining });
        return;
      }

      const best = candidates.reduce((a, b) => (outranks(b, a) ? b : a));

      // Another space's request already owns the flow (pending or held): writing `best`
      // through `_requestedCelebration`/`open` would silently replace that space's
      // celebration. Hand this space's winner back to the queue instead -- the tab's own
      // consume effect re-fires (its `open` dependency) once that space's held celebration
      // ends, or on next mount, and picks it up then.
      if (requested !== null && requested.spaceId !== spaceId && flowState !== 'idle') {
        set({ pendingCelebrations: [...remaining, best] });
        return;
      }

      if (flowState === 'held') {
        // Swap the payload under the live grant only when a fresher/higher-rank milestone
        // actually outranks what's already on screen.
        set(
          best === requested
            ? { pendingCelebrations: remaining }
            : {
                pendingCelebrations: remaining,
                _requestedCelebration: best,
                open: celebrationToOpen(best),
              },
        );
        return;
      }

      set({ pendingCelebrations: remaining, _requestedCelebration: best });
      if (flowState === 'idle') {
        celebrationFlow.request({
          // Re-reads at grant time: the winner might go stale while queued behind a takeover
          // or a held drawer.
          stillNeeded: () => {
            if (celebrationFlow.getState() === 'idle') return false;
            const c = get()._requestedCelebration;
            return c !== null && Date.now() - c.queuedAt < CELEBRATION_FRESHNESS_MS;
          },
        });
      }
      // 'pending': the payload write above is all that changes.
    },
    close: (reason = 'programmatic') => {
      // A pending sibling survives so the drain grants it next (§6.6).
      const drawerHeld = drawerFlow.getState() === 'held';
      const celebrationHeld = celebrationFlow.getState() === 'held';
      if (drawerHeld) drawerFlow.end(reason);
      else if (celebrationHeld) celebrationFlow.end(reason);
      else get().closeAll();
    },
    closeAll: () => {
      // Unlike close(), this ends BOTH flows regardless of held/pending -- a hard reset for
      // the auth-route sweep (transientOverlays), where nothing queued may land on the door.
      celebrationFlow.end('programmatic');
      drawerFlow.end('programmatic');
      set({ open: null });
    },
    closeDeferringCelebration: () => {
      // For a CTA that navigates away or opens another local surface instead of just
      // dismissing: a celebration on screen, or merely PENDING behind the drawer, must not
      // land on whatever the CTA opens next (Setup, the choice drawer). A pending one is
      // handed back to pendingCelebrations, keeping its original queuedAt so the freshness
      // window still applies -- the tab's consume effect re-offers it if the owner returns
      // in time. A held one is simply ended, since it's already on screen and would just
      // strand the mutex slot otherwise.
      const celebrationState = celebrationFlow.getState();
      const requested = get()._requestedCelebration;
      if (celebrationState === 'pending' && requested !== null) {
        set(state => ({ pendingCelebrations: [...state.pendingCelebrations, requested] }));
      }
      celebrationFlow.end('programmatic');
      drawerFlow.end('programmatic');
      set({ open: null });
    },
    setBackHandler: fn => {
      drawerBackHandler = fn;
    },
    setActionFlow: (subStepId, spaceId) => {
      set({ actionFlow: { subStepId, spaceId, armedAt: Date.now() } });
    },
    clearActionFlow: () => {
      set({ actionFlow: null });
    },
  };
});

/**
 * True when the pending action-flow is exactly this sub-step for this space AND still fresh.
 * Used by all five action sites (CreateEvent, SetupSpace ×2, Create Question, AdminInvite)
 * to confirm a just-finished action is part of the owner's activation flow before firing
 * completion. The freshness guard is what protects every consumer from a stale token left
 * armed by an abandoned flow (see ACTION_FLOW_FRESHNESS_MS).
 */
export function matchesActionFlow(
  actionFlow: { subStepId: ActivationSubStepId; spaceId: string; armedAt: number } | null,
  subStepId: ActivationSubStepId,
  spaceId: string,
): boolean {
  if (actionFlow == null || actionFlow.subStepId !== subStepId || actionFlow.spaceId !== spaceId) {
    return false;
  }
  // The sub-step + space match. The token must ALSO be fresh: an owner who armed the flow
  // (tapped the activation CTA) then abandoned before reaching this action site would
  // otherwise leave a stale token that mis-attributes a much later manual action in the
  // same space. `actionFlow.armedAt` is a Date.now() stamp; ACTION_FLOW_FRESHNESS_MS bounds
  // the honored window (strict `<`, matching the celebration queue's own freshness check).
  return Date.now() - actionFlow.armedAt < ACTION_FLOW_FRESHNESS_MS;
}
